import GridPage from "components/layout/GridPage";
import {
  Box,
  Typography,
  Button,
  Divider,
  FormControl,
  MenuItem,
} from "@mui/material";
import { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import { ArrowLeft } from "lucide-react";
import ClientOutputVisibleIndicator from "components/tools/ClientOutputVisibleIndicator";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import { useCreateAgentPrompt, useToggleAgentPrompt } from "services/agents";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import Select from "components/common/Select";
import TextField from "components/common/TextField";
import { CreateAgentPromptRequest } from "protogen/agents_service_pb";
import AgentToolsSelector from "components/tools/AgentToolsSelector";

type ToolsConfiguration = {
  always?: string[];
  never?: string[];
};

const TEMPLATE = `
<p>You are great at analyzing the clients details and calendar to add or edit entries in a calendar. You always flag conflicts.</p>
<p></p>
<p><strong>High-quality plans</strong></p>
<p></p>
<p>Example 1:</p>
<p>user's ask: Add the events from this website to my calendar</p>
<p>Plan:</p>
<p>1. Lookup client details</p>
<p>2. Extract events from the website</p>
<p>3. Verify the events have the correct data</p>
<p>4. Use tool to add events</p>
<p>5. Identify any potential conflicts and share with user</p>
<p></p>
`;

export default () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState<RichContent | null>(null);
  const [scope, setScope] = useState("public");
  const [clientOutputVisible, setClientOutputVisible] = useState(false);
  const [nameError, setNameError] = useState("");
  const [instructionsError, setInstructionsError] = useState("");
  const [toolsConfig, setToolsConfig] = useState<ToolsConfiguration>({});
  const editorRef = useRef<Handle>(null);
  const { request: createAgentPrompt, loading: createLoading } =
    useCreateAgentPrompt();
  const { request: toggleAgentPrompt } = useToggleAgentPrompt();
  const isAdmin = isUserAdmin(currentUser);

  const buildConfigurationJson = (): string => {
    const hasToolsConfig =
      (toolsConfig.always?.length || 0) + (toolsConfig.never?.length || 0) > 0;
    const config = {
      ...(hasToolsConfig ? { tools: toolsConfig } : {}),
    };
    return Object.keys(config).length > 0 ? JSON.stringify(config) : "";
  };

  const handleCreate = async () => {
    let hasError = false;

    if (!name.trim()) {
      setNameError("Name is required");
      hasError = true;
    } else {
      setNameError("");
    }

    if (!instructions || !instructions.text?.trim()) {
      setInstructionsError("Instructions are required");
      hasError = true;
    } else {
      setInstructionsError("");
    }

    if (hasError) return;

    const response = await createAgentPrompt(
      new CreateAgentPromptRequest({
        type: "playbook",
        name: name.trim(),
        status: "active",
        scope: scope,
        clientOutputVisible: clientOutputVisible,
        content: {
          contentType: "html",
          payload: instructions?.html || "",
          textContent: instructions?.text || "",
          attachments: instructions?.attachments || [],
        },
        configurationJson: buildConfigurationJson(),
      }),
    );

    if (response?.prompt) {
      await toggleAgentPrompt({
        promptRef: response.prompt.ref,
        isEnabled: true,
      });
      navigate(
        `/tools/agents/playbooks/${encodeURIComponent(
          response.prompt.ref,
        )}?new=1`,
      );
    }
  };

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "64px auto",
        maxWidth: "800px",
        padding: isMobile ? "20px" : "0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box>
          <Button
            variant="text"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate("/tools/agents/playbooks")}
            sx={{ marginBottom: "16px", padding: "4px 8px" }}
          >
            Back to playbooks
          </Button>
          <Typography variant="h3" sx={{ marginBottom: "8px" }}>
            Add new playbook
          </Typography>
        </Box>

        <Divider />

        <Box>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError("");
            }}
            fullWidth
            required
            autoFocus
            error={!!nameError}
            helperText={nameError}
          />
        </Box>

        <Box display="flex" alignItems="center" gap="16px">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              size="small"
              sx={{ fontSize: "14px" }}
            >
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              {isAdmin && (
                <MenuItem value="system-default">System Default</MenuItem>
              )}
            </Select>
          </FormControl>
          <AgentToolsSelector
            value={toolsConfig}
            onChange={setToolsConfig}
            disabled={createLoading}
          />
          <ClientOutputVisibleIndicator
            visible={clientOutputVisible}
            onClick={() => setClientOutputVisible(!clientOutputVisible)}
            size={20}
          />
        </Box>
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="12px"
          >
            <Typography variant="bodyHeavy">Instructions</Typography>
          </Box>
          <SimpleRichEditor
            ref={editorRef}
            setContent={(content) => {
              setInstructions(content);
              if (instructionsError) setInstructionsError("");
            }}
            initialContent={TEMPLATE}
            placeholder="Enter detailed instructions for this playbook..."
            editorMinHeight="200px"
            forceOpen={true}
            allowAttachments={false}
            isMarkdownTextMode={true}
          />
          {instructionsError && (
            <Typography
              variant="body"
              color="error"
              sx={{ marginTop: "8px", fontSize: "0.75rem" }}
            >
              {instructionsError}
            </Typography>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" gap="8px">
          <Button
            variant="text"
            onClick={() => navigate("/tools/agents/playbooks")}
          >
            Cancel
          </Button>
          <Button
            disabled={
              !name.trim() ||
              !instructions?.text?.trim() ||
              instructions?.text?.trim() === TEMPLATE.trim() ||
              createLoading
            }
            onClick={handleCreate}
          >
            Create
          </Button>
        </Box>
      </Box>
    </GridPage>
  );
};
